import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { IInitialState } from "./store/reducers/models";
import { message, Button, Tooltip, Badge } from "antd";
import { setError } from "./store/reducers";
import Auth from "./containers/Auth";
import Layout from "./containers/Layout";
import Departments from "./containers/Departments";
import Admin from "./containers/Admin";
import Team from "./containers/Team";
import Reports from "./containers/Reports";
import Search from "./containers/Reports/search";
import Sales from "./containers/Sales";
import Leads from "./containers/Leads";
import Dashboard from "./containers/Dashboard";
import DepartmentForm from "./containers/Departments/Form";
import AdminForm from "./containers/Admin/Form";
import TeamForm from "./containers/Team/Form";
import Lead from "./containers/Leads/Lead";
import ProtectedRoutes from "./components/ProtectedRoutes";
import SalesData from "./containers/Sales/Form";
import Referals from "./containers/Referals";
import SalesDataReports from "./containers/Sales/Sales-Report";
import Configure from "./containers/Configure";
import Notifications from "./containers/Notifications";
import TaxConfiguration from "./containers/TaxConfiguration";
import UnAuthorized from "./components/UnAuthorized";
import NotFound from "./components/NotFound";
import TeamLead from "./containers/TeamLead";
import TeamLeadForm from "./containers/TeamLead/Form";
import Support from "./containers/Support";
import Escalation from "./containers/Escalation";
import Comments from "./containers/Support/Form/Comments";
import CommentsUser from "./containers/Escalation/Form/Comments";
import GlobalFollowUpReminder from "./containers/Sales/Sales-Report/GlobalFollowUpReminder";
import SalesService from "./services/Sales.services";
import { BellOutlined } from "@ant-design/icons";
import "./styles.scss";
import "./followupButton.css";

const routerComponents = [
  { path: "/dashboard", Component: Dashboard },
  { path: "/departments", Component: Departments },
  { path: "/departments/create", Component: DepartmentForm },
  { path: "/departments/edit/:id", Component: DepartmentForm },
  { path: "/admin", Component: Admin },
  { path: "/admin/create", Component: AdminForm },
  { path: "/admin/edit/:id", Component: AdminForm },
  { path: "/teamlead", Component: TeamLead },
  { path: "/teamlead/create", Component: TeamLeadForm },
  { path: "/teamlead/edit/:id", Component: TeamLeadForm },
  { path: "/team", Component: Team },
  { path: "/team/create", Component: TeamForm },
  { path: "/team/edit/:id", Component: TeamForm },
  { path: "/reports", Component: Reports },
  { path: "/search", Component: Search },
  { path: "/configuration", Component: Configure },
  { path: "/sales", Component: Sales },
  { path: "/sales/data-upload", Component: SalesData },
  { path: "/sales/report", Component: SalesDataReports },
  { path: "/sales/followup-calls", Component: SalesDataReports },
  { path: "/leads", Component: () => <Leads isLeads /> },
  { path: "/walkouts", Component: () => <Leads isLeads={false} /> },
  { path: "/leads/create", Component: Lead },
  { path: "/leads/edit/:id", Component: Lead },
  { path: "/referals", Component: Referals },
  { path: "/notifications", Component: Notifications },
  {
    path: "/tax-configuration",
    Component: () => (
      <TaxConfiguration
        section={"TAX_CONFIG"}
        leadData={null}
        showHeader={true}
      />
    ),
  },
  { path: "/support", Component: Support },
  { path: "/support/view/:id", Component: Comments },
  { path: "/escalation", Component: Escalation },
  { path: "/escalation/view/:id", Component: CommentsUser },
];

function App() {
  const [messageApi, contextHolder] = message.useMessage();
  const gloablStore = useSelector((state: any) => state.store);
  const { showAlert, alertMessage, alertType }: IInitialState = gloablStore;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ Parse user details correctly
  const storedUser = localStorage.getItem("user_details");
  const userDetails = storedUser ? JSON.parse(storedUser) : null;

  // ✅ State for Follow-up Reminder
  const [showReminder, setShowReminder] = useState(false);
  const [hasNewFollowups, setHasNewFollowups] = useState(false);
  const [reminders, setReminders] = useState(0);

  const fetchFollowups = async () => {
    try {
      const response = await SalesService.getsalesList(
        `/sales/follow-ups?notification=true`,
      );
      const { totalRows } = response || {};
      setReminders(totalRows || 0);
      setHasNewFollowups(totalRows > 0);
    } catch (error) {
      console.error("Error fetching follow-ups:", error);
    }
  };

  useEffect(() => {
    if (userDetails?.role === "3" && userDetails?.departmentId === 1) {
      fetchFollowups();

      const interval = setInterval(fetchFollowups, 5 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [userDetails]);

  useEffect(() => {
    if (showAlert) {
      messageApi.open({ type: alertType, content: alertMessage });
      setTimeout(() => {
        dispatch(setError({ status: false, type: undefined, message: "" }));
      }, 1000);
    }
  }, [showAlert, alertType, alertMessage, dispatch, messageApi]);

  return (
    <div className="App">
      <ToastContainer />
      {contextHolder}

      <Routes>
        <Route path="/" element={<Auth />} />
        <Route path="/unauthorized" element={<UnAuthorized />} />
        <Route element={<Layout />}>
          {routerComponents.map(({ path, Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoutes>
                  <Component />
                </ProtectedRoutes>
              }
            />
          ))}
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* ✅ Floating Follow-up Button — only for role == 3 */}
      {userDetails &&
        userDetails.role === "3" &&
        !["/", "/unauthorized", "/notfound"].includes(
          window.location.pathname,
        ) && (
          <div className="followup-badge-wrapper">
            <Tooltip title="View Follow-ups">
              <Badge
                count={hasNewFollowups ? reminders : 0}
                overflowCount={99}
                size="small"
                style={{
                  backgroundColor: "#FFFF00",
                  color: "#000",
                  boxShadow: "0 0 4px rgba(0,0,0,0.3)",
                  padding: 5,
                  borderRadius: "50%",
                  width: 25,
                  height: 25,
                  fontSize: 14,
                }}
              >
                <Button
                  shape="circle"
                  size="large"
                  className={`followup-btn ${hasNewFollowups ? "bounce" : ""}`}
                  onClick={() => setShowReminder(true)}
                >
                  <BellOutlined
                    style={{
                      fontSize: "26px",
                      color: hasNewFollowups ? "#ff4d4f" : "#1890ff",
                      transition: "color 0.3s ease",
                    }}
                  />
                </Button>
              </Badge>
            </Tooltip>
          </div>
        )}

      {/* ✅ Show modal only on click */}
      <GlobalFollowUpReminder
        visible={showReminder}
        onClose={() => setShowReminder(false)}
      />
    </div>
  );
}

export default App;
